/*
 * "Hoy en vivo": gasto, leads, tratos y matrículas del día en curso, leídos
 * directo del CRM y de Meta. Es solo informativo — nunca calcula semáforos ni
 * recomendaciones, porque con el día a medias el CPL sale engañoso y Meta
 * todavía ajusta las cifras del día durante horas. Esas decisiones siguen
 * saliendo únicamente del corte oficial cerrado (pestaña Matriz por pieza).
 * El backend refresca el gasto de Meta como máximo cada 15 minutos mientras
 * alguien tenga este bloque abierto — no hay tarea programada en el servidor.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import authAxios from '../../../../config/authAxios'

const REFRESH_MS = 15 * 60 * 1000

const money = value => value === null || value === undefined ? '—' : `S/ ${Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const number = value => value === null || value === undefined ? '—' : Number(value).toLocaleString('es-PE')
const shortTime = value => value ? String(value).slice(11, 16) : '—'

const HoyEnVivoAgente = ({ campaignId }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const intervalRef = useRef(null)

  const load = useCallback((force = false) => {
    if (!campaignId) return
    setLoading(true); setError('')
    authAxios.get(`/admin/agente-marketing/hoy?id_camp=${campaignId}${force ? '&forzar=1' : ''}`)
      .then(r => setData(r.data?.data || null))
      .catch(e => setError(e.response?.data?.msg || 'No se pudo cargar el dato en vivo.'))
      .finally(() => setLoading(false))
  }, [campaignId])

  useEffect(() => {
    load()
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => load(), REFRESH_MS)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [load])

  if (!campaignId) return null

  const fuentesConError = Object.entries(data?.estado_fuentes || {}).filter(([, v]) => v?.estado === 'error')

  return <div className="am-section">
    <div className="am-section-title">
      <h5>Hoy en vivo <span className="am-badge am-badge-sin_datos ml-2">Parcial — no es corte oficial</span></h5>
      <div className="text-right">
        <small className="text-muted d-block">Actualizado {shortTime(data?.actualizado_en)}</small>
        <button className="btn btn-sm btn-outline-secondary mt-1" disabled={loading} onClick={() => load(true)}>
          {loading ? 'Actualizando…' : 'Actualizar ahora'}
        </button>
      </div>
    </div>

    {error && <div className="alert alert-danger py-2">{error}</div>}

    {fuentesConError.length > 0 && <div className="am-source bad mb-3">
      {fuentesConError.map(([plat, v]) => <div key={plat}><strong className="text-uppercase">{plat}</strong>: {v.mensaje || 'no respondió'}. Se muestran los últimos datos disponibles.</div>)}
    </div>}

    {data && <>
      <div className="row mb-3">
        <div className="col-6 col-md-4 col-lg mb-2"><div className="am-card"><small>Gasto de hoy</small><strong>{money(data.gasto)}</strong></div></div>
        <div className="col-6 col-md-4 col-lg mb-2"><div className="am-card"><small>Leads de hoy</small><strong>{number(data.leads)}</strong></div></div>
        <div className="col-6 col-md-4 col-lg mb-2"><div className="am-card"><small>Tratos de hoy</small><strong>{number(data.tratos)}</strong></div></div>
        <div className="col-6 col-md-4 col-lg mb-2"><div className="am-card">
          <small>Matrículas de hoy</small>
          <strong>{number(data.matriculas_total)}</strong>
          {data.matriculas_total > 0 && <span className="d-block text-muted" style={{ fontSize: '.78rem' }}>
            {data.matriculas_pauta} pauta{data.detalle_matriculas?.cartera > 0 && ` · ${data.detalle_matriculas.cartera} cartera`}{data.detalle_matriculas?.previos > 0 && ` · ${data.detalle_matriculas.previos} previos`}
          </span>}
        </div></div>
        <div className="col-6 col-md-4 col-lg mb-2"><div className="am-card">
          <small>CPL parcial</small>
          <strong>{money(data.cpl_parcial)}</strong>
          <span className="d-block text-muted" style={{ fontSize: '.72rem' }}>referencial, cambia durante el día</span>
        </div></div>
      </div>

      <div className="am-table-wrap">
        <table className="table table-sm am-table">
          <thead><tr><th>Pieza</th><th>Plataforma</th><th className="text-right">Gasto hoy</th><th className="text-right">Leads hoy</th></tr></thead>
          <tbody>
            {(data.piezas || []).filter(p => p.gasto > 0 || p.leads > 0).map(p => <tr key={p.id_creative}>
              <td>{p.nombre}{!p.activa && <small className="d-block text-muted">pausada</small>}</td>
              <td className="text-uppercase">{p.plataforma}</td>
              <td className="text-right">{money(p.gasto)}</td>
              <td className="text-right">{number(p.leads)}</td>
            </tr>)}
          </tbody>
        </table>
        {(data.piezas || []).every(p => p.gasto === 0 && p.leads === 0) && <p className="text-center text-muted py-2">Todavía no hay movimiento hoy.</p>}
      </div>

      <p className="text-muted mt-2 mb-0" style={{ fontSize: '.75rem' }}>Los semáforos y las recomendaciones usan solo el corte cerrado del día anterior.</p>
    </>}
  </div>
}

export default HoyEnVivoAgente
